import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import api, { formatApiError, resolveImageUrl } from "@/lib/api";
import { toast } from "sonner";
import PageMeta from "@/components/PageMeta";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return "";
  }
}

export default function ArticleDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/articles/${slug}`)
      .then((r) => setArticle(r.data))
      .catch((e) => {
        if (e?.response?.status === 404) setNotFound(true);
        else toast.error(formatApiError(e));
      })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-6 lg:px-12 py-20 text-muted-foreground text-sm">Loading…</div>
    );
  }

  if (notFound || !article) {
    return (
      <div className="max-w-3xl mx-auto px-6 lg:px-12 py-20" data-testid="article-not-found">
        <h1 className="font-display text-3xl font-semibold tracking-tight">Article not found</h1>
        <p className="mt-3 text-muted-foreground">The article you're looking for doesn't exist or was unpublished.</p>
        <div className="mt-6">
          <Button variant="outline" onClick={() => navigate("/articles")} data-testid="back-to-articles-btn">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to articles
          </Button>
        </div>
      </div>
    );
  }

  return (
    <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-12 py-10 sm:py-16 lg:py-24" data-testid={`article-${article.slug}`}>
      <PageMeta
        title={article.title}
        description={article.excerpt || `An article by ${article.author || "Evan Gill"} on breathwork for performance.`}
        image={article.cover_image_url ? (article.cover_image_url.startsWith("http") ? article.cover_image_url : `${process.env.REACT_APP_BACKEND_URL}${article.cover_image_url}`) : undefined}
        type="article"
      />
      <Link to="/articles" className="inline-flex items-center text-sm text-muted-foreground hover:text-primary transition-colors mb-6 sm:mb-8" data-testid="back-to-articles-link">
        <ArrowLeft className="h-4 w-4 mr-1" /> All articles
      </Link>

      <p className="overline text-primary mb-3">Insight</p>
      <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.1]">
        {article.title}
      </h1>
      <div className="mt-3 sm:mt-4 text-sm text-muted-foreground">
        {formatDate(article.published_at || article.created_at)}
        {article.author ? ` · ${article.author}` : ""}
      </div>

      {article.cover_image_url && (
        <div className="mt-8 sm:mt-10 rounded-xl sm:rounded-2xl overflow-hidden bg-muted aspect-[16/9]">
          <img
            src={resolveImageUrl(article.cover_image_url)}
            alt={article.title}
            className="w-full h-full object-cover"
            data-testid="article-cover-image"
          />
        </div>
      )}

      {article.excerpt && (
        <p className="mt-8 text-base sm:text-lg text-muted-foreground leading-relaxed border-l-2 border-primary/40 pl-4 sm:pl-5">
          {article.excerpt}
        </p>
      )}

      <div
        className="mt-8 sm:mt-10 text-foreground/90 leading-[1.85] whitespace-pre-wrap text-base sm:text-lg"
        data-testid="article-body"
      >
        {article.body}
      </div>

      <div className="mt-12 sm:mt-16 pt-6 sm:pt-8 border-t border-border">
        <Link to="/articles">
          <Button variant="outline" className="rounded-full" data-testid="article-more-btn">
            Read more articles
          </Button>
        </Link>
      </div>
    </article>
  );
}
